import React, { useState, useCallback, useEffect } from "react";
import { upgradePlan, PLANS } from "../billingApi";
import { track } from "../analytics";
import "./UpgradeModal.css";

// Lowest to highest tier — used to label a plan change as "Downgrade to X"
// vs. "Choose X" and to disable the current plan's card.
const PLAN_ORDER = ["trial", "starter", "growth", "scale"];

// ── Feature comparison table data ────────────────────────────────────
const COMPARE_ROWS = [
  { label: "Leads",                  trial: "25",        starter: "100",    growth: "1,000",   scale: "Unlimited" },
  { label: "WhatsApp follow-up tiers", trial: "2 tiers", starter: "4 tiers", growth: "6 tiers", scale: "Custom" },
  { label: "Messages/month",          trial: "100",       starter: "500",    growth: "5,000",   scale: "Unlimited" },
  { label: "Payment links",           trial: "✓",         starter: "✓",      growth: "✓",       scale: "✓" },
  { label: "Pipeline dashboard",      trial: "Basic",     starter: "Full",   growth: "Full",    scale: "Full" },
  { label: "Activity timeline",       trial: "24h",       starter: "7 days", growth: "90 days", scale: "1 year" },
  { label: "Control Room execution",  trial: "—",         starter: "—",      growth: "✓",       scale: "✓" },
  { label: "Developer & Business OS", trial: "—",         starter: "—",      growth: "✓",       scale: "✓" },
  { label: "Support",                 trial: "—",         starter: "Email",  growth: "Priority", scale: "Dedicated" },
  { label: "SLA uptime",             trial: "—",         starter: "—",      growth: "—",       scale: "✓" },
];

// ── Plan card ─────────────────────────────────────────────────────────
function PlanCard({ plan, selected, onSelect, loading, currentPlan }) {
  const isScale = plan.id === "scale";
  const isCurrent = plan.id === currentPlan;

  return (
    <div
      className={`um-plan${plan.featured ? " um-plan--featured" : ""}${selected === plan.id ? " um-plan--selected" : ""}${isCurrent ? " um-plan--current" : ""}`}
      onClick={() => !isScale && !isCurrent && onSelect(plan.id)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && !isScale && !isCurrent && onSelect(plan.id)}
      aria-pressed={selected === plan.id}
    >
      {isCurrent && <div className="um-plan-badge um-plan-badge--current">Current plan</div>}
      {!isCurrent && plan.badge && <div className="um-plan-badge">{plan.badge}</div>}
      <h3 className="um-plan-name">{plan.name}</h3>
      <p className="um-plan-tagline">{plan.tagline}</p>
      <div className="um-plan-price-row">
        <span className="um-plan-price">{plan.price}</span>
        {plan.period && <span className="um-plan-period">{plan.period}</span>}
      </div>
      <ul className="um-plan-features">
        {plan.features.slice(0, 5).map((f, i) => (
          <li key={i} className="um-plan-feature">
            <span className="um-plan-check" aria-hidden="true">✓</span>
            {f}
          </li>
        ))}
        {plan.features.length > 5 && (
          <li className="um-plan-feature um-plan-feature--more">
            +{plan.features.length - 5} more
          </li>
        )}
      </ul>
      {isCurrent ? (
        <button className="um-plan-cta" disabled>Current plan</button>
      ) : isScale ? (
        <a
          className="um-plan-cta um-plan-cta--contact"
          href="mailto:sales@ooplix.com"
          onClick={e => e.stopPropagation()}
        >
          Contact Sales
        </a>
      ) : (
        <button
          className={`um-plan-cta${plan.featured ? " um-plan-cta--featured" : ""}`}
          onClick={e => { e.stopPropagation(); onSelect(plan.id); }}
          disabled={loading}
        >
          {selected === plan.id && loading
            ? "Processing…"
            : PLAN_ORDER.indexOf(plan.id) < PLAN_ORDER.indexOf(currentPlan)
              ? `Downgrade to ${plan.name}`
              : `Choose ${plan.name}`}
        </button>
      )}
    </div>
  );
}

// ── Comparison table ─────────────────────────────────────────────────
function CompareTable() {
  return (
    <div className="um-compare">
      <div className="um-compare-scroll">
        <table className="um-compare-table">
          <thead>
            <tr>
              <th className="um-th um-th--feature">Feature</th>
              <th className="um-th">Trial</th>
              <th className="um-th">Starter</th>
              <th className="um-th um-th--highlight">Growth</th>
              <th className="um-th">Scale</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map(row => (
              <tr key={row.label} className="um-tr">
                <td className="um-td um-td--feature">{row.label}</td>
                <td className="um-td um-td--dim">{row.trial}</td>
                <td className="um-td">{row.starter}</td>
                <td className="um-td um-td--highlight">{row.growth}</td>
                <td className="um-td">{row.scale}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Root modal ────────────────────────────────────────────────────────
export default function UpgradeModal({ open, onClose, onSuccess, billing }) {
  const [selected,    setSelected]    = useState("growth"); // pre-select recommended
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const [showTable,   setShowTable]   = useState(false);

  // Track open
  useEffect(() => {
    if (open) {
      track.event("upgrade_modal_opened", {
        trigger: "manual",
        current_plan: billing?.plan || "trial",
        days_left: billing?.daysLeft,
      });
    }
  }, [open, billing]);

  const handleUpgrade = useCallback(async (planId) => {
    if (planId === "scale") return; // handled by mailto link in card
    setLoading(true);
    setError(null);
    setIsAuthError(false);
    setSelected(planId);

    track.event("upgrade_plan_selected", { plan: planId });

    const res = await upgradePlan(planId);
    setLoading(false);

    if (res?.success && res?.paymentUrl) {
      track.paymentStarted(res.amount || 0);
      window.open(res.paymentUrl, "_blank", "noopener,noreferrer");
      onSuccess?.();
      onClose?.();
    } else {
      // A.6 business-owner-journey finding: this used to replace the real
      // backend error with the literal sentinel string "payment_auth_failed"
      // for any auth-class failure, discarding res?.error entirely before
      // it ever reached the UI — the founder saw only "Payment processing
      // is temporarily unavailable," never the real reason. Confirmed
      // live: a real upgrade attempt against real (but invalid) Razorpay
      // keys returned {"error":"Authentication failed"} from the backend
      // (the exact detail paymentService.js's own catch-block fix now
      // correctly surfaces), and this modal still showed nothing but the
      // generic message. Per this pass's explicit rule — expose the real
      // infrastructure error, never convert it to a generic one — the
      // real message is now kept and shown alongside the actionable
      // "email us" guidance, not replaced by it.
      const rawErr = res?.error || "Could not initiate payment. Please try again or contact support.";
      const isAuthErr = rawErr.toLowerCase().includes("authentication") ||
                        rawErr.toLowerCase().includes("401") ||
                        rawErr.toLowerCase().includes("not configured");
      setError(rawErr);
      setIsAuthError(isAuthErr);
    }
  }, [onClose, onSuccess]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="um-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Upgrade plan"
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="um-panel animate-scale-in">

        {/* Header */}
        <div className="um-header">
          <div className="um-header-copy">
            <h2 className="um-title">Upgrade Ooplix</h2>
            <p className="um-subtitle">
              Keep your automations running. Choose a plan to continue after your trial.
            </p>
          </div>
          <button className="um-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Trial status context */}
        {billing && (billing.status === "trialing" || billing.graceActive || billing.status === "expired") && (
          <div className={`um-trial-context um-trial-context--${billing.graceActive ? "grace" : billing.daysLeft <= 1 ? "critical" : "info"}`}>
            {billing.graceActive
              ? "⚠ You're in the 24-hour grace period. Upgrade now to avoid losing access."
              : billing.daysLeft === 0
              ? "⚠ Trial expires today."
              : billing.daysLeft === 1
              ? `⚠ 1 day left on your trial.`
              : `${billing.daysLeft} days left on your trial.`}
          </div>
        )}

        {/* Plan cards */}
        <div className="um-plans">
          {PLANS.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              selected={selected}
              onSelect={handleUpgrade}
              loading={loading}
              currentPlan={billing?.status === "active" ? billing?.plan : null}
            />
          ))}
        </div>

        {/* Error — rich block for payment auth failure, plain text for others.
            Both now include the real error text (see handleUpgrade) — the
            rich block adds actionable "email us" guidance on top of the
            real reason, it no longer replaces the real reason with generic
            copy. */}
        {error && isAuthError && (
          <div className="um-error um-error--rich" role="alert">
            <span className="um-error-icon">⚠</span>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Payment processing is temporarily unavailable.</div>
              <div style={{ fontSize: "0.82rem", lineHeight: 1.55, marginBottom: 4, opacity: 0.85 }}>
                Reason: {error}
              </div>
              <div style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
                To upgrade now, email us and we'll send you a payment link directly:
                {" "}<a href="mailto:billing@ooplix.com?subject=Upgrade request&body=Plan: " className="um-error-link">billing@ooplix.com</a>
              </div>
            </div>
          </div>
        )}
        {error && !isAuthError && (
          <div className="um-error" role="alert">
            <span className="um-error-icon">⚠</span>
            {error}
            {" — "}
            <a href="mailto:billing@ooplix.com" className="um-error-link">contact billing ↗</a>
          </div>
        )}

        {/* Feature comparison toggle */}
        <div className="um-compare-toggle">
          <button
            className="um-toggle-btn"
            onClick={() => setShowTable(t => !t)}
            aria-expanded={showTable}
          >
            {showTable ? "Hide" : "See"} full feature comparison {showTable ? "↑" : "↓"}
          </button>
        </div>

        {showTable && <CompareTable />}

        {/* Trust footer */}
        <div className="um-footer">
          <span>🔒 Payments via Razorpay — PCI-DSS Level 1</span>
          <span>·</span>
          <span>Cancel anytime</span>
          <span>·</span>
          <span>Data retained 30 days after cancellation</span>
        </div>

      </div>
    </div>
  );
}
